/**
 * Admin server functions for feature flag management. Admin auth and input
 * parsing only; the logic lives in `~/lib/server/admin-feature-flags`.
 *
 * Aliases: admin feature flags, flag management, manage flags
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  type AdminFeatureFlagRow,
  featureFlagUpdateSchema,
  type FlagUser,
} from "~/lib/shared/feature-flags";

export const adminFeatureFlagQueryKeys = {
  all: ["admin-feature-flags"] as const,
  userSearch: (term: string) =>
    ["admin-feature-flags", "user-search", term] as const,
};

export const getFeatureFlagsAdminFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("feature-flags:read")])
  .handler(async (): Promise<AdminFeatureFlagRow[]> => {
    const { listAdminFeatureFlags } =
      await import("~/lib/server/admin-feature-flags");
    return listAdminFeatureFlags();
  });

export const adminFeatureFlagsQueryOptions = queryOptions({
  queryKey: adminFeatureFlagQueryKeys.all,
  queryFn: () => getFeatureFlagsAdminFn(),
});

export const updateFeatureFlagFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("feature-flags:write")])
  .validator(featureFlagUpdateSchema)
  .handler(async ({ data }): Promise<AdminFeatureFlagRow> => {
    const { updateFeatureFlag } =
      await import("~/lib/server/admin-feature-flags");
    const { after } = await updateFeatureFlag(data);
    return after;
  });

export const searchUsersForFlagFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("feature-flags:read")])
  .validator(
    z.object({
      search: z.string().min(2),
      exclude: z.array(z.string().uuid()).default([]),
    }),
  )
  .handler(async ({ data }): Promise<FlagUser[]> => {
    const { searchFlagUsers } =
      await import("~/lib/server/admin-feature-flags");
    return searchFlagUsers(data);
  });
